/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical"
import { $applyNodeReplacement, DecoratorNode } from "lexical"
import * as React from "react"

import { ImageSettingsForm } from "./FloatingImageEditor/FloatingImageEditor"
import InlineImageComponent from "./InlineImageComponent"

export type InlineImagePayload = {
  key?: NodeKey
  src: string
  width?: number
  height?: number
  link?: string
  isRounded?: boolean
  fileName?: string
  fileType?: string
  topText?: string
  topTextColor?: string
  bottomText?: string
  bottomTextColor?: string
}

export type SerializedInlineImageNode = Spread<
  {
    height?: number
    src: string
    width?: number
    isRounded?: boolean
    link?: string
    fileName?: string
    fileType?: string
    topText?: string
    topTextColor?: string
    bottomText?: string
    bottomTextColor?: string
  },
  SerializedLexicalNode
>

export class InlineImageNode extends DecoratorNode<JSX.Element> {
  __src: string
  __link?: string
  __width?: number
  __height?: number
  __isRounded?: boolean
  __fileName?: string
  __fileType?: string
  __topText?: string
  __topTextColor?: string
  __bottomText?: string
  __bottomTextColor?: string

  constructor(
    src: string,
    key?: NodeKey,
    link?: string,
    width?: number,
    height?: number,
    isRounded?: boolean,
    fileName?: string,
    fileType?: string,
    topText?: string,
    topTextColor?: string,
    bottomText?: string,
    bottomTextColor?: string
  ) {
    super(key)
    this.__src = src
    this.__link = link
    this.__width = width
    this.__height = height
    this.__isRounded = isRounded
    this.__handleImageSettingsChange = this.__handleImageSettingsChange.bind(this)
    this.__fileName = fileName
    this.__fileType = fileType
    this.__topText = topText
    this.__topTextColor = topTextColor
    this.__bottomText = bottomText
    this.__bottomTextColor = bottomTextColor
  }

  static override getType(): string {
    return "inline-image"
  }

  static override clone(node: InlineImageNode): InlineImageNode {
    return new InlineImageNode(
      node.__src,
      node.__key,
      node.__link,
      node.__width,
      node.__height,
      node.__isRounded,
      node.__fileName,
      node.__fileType,
      node.__topText,
      node.__topTextColor,
      node.__bottomText,
      node.__bottomTextColor
    )
  }

  static override importJSON(serializedNode: SerializedInlineImageNode): InlineImageNode {
    const {
      height,
      width,
      src,
      isRounded,
      link,
      fileName,
      fileType,
      topText,
      topTextColor,
      bottomText,
      bottomTextColor,
    } = serializedNode
    const node = $createInlineImageNode({
      height,
      src,
      width,
      isRounded,
      link,
      fileName,
      fileType,
      topText,
      topTextColor,
      bottomText,
      bottomTextColor,
    })

    return node
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => {
        return {
          conversion: convertInlineImageElement,
          priority: 0,
        }
      },
    }
  }

  override exportDOM(): DOMExportOutput {
    const element = document.createElement("img")

    element.setAttribute("src", this.__src)
    element.setAttribute("data-link", this.__link || "")
    element.setAttribute("data-file-name", this.__fileName || "")
    element.setAttribute("data-file-type", this.__fileType || "")
    element.setAttribute("data-top-text", this.__topText || "")
    element.setAttribute("data-top-text-color", this.__topText || "")
    element.setAttribute("data-bottom-text", this.__bottomText || "")
    element.setAttribute("data-bottom-text-color", this.__bottomText || "")

    element.style.borderRadius = this.__isRounded ? "50%" : "0"

    return { element }
  }

  override exportJSON(): SerializedInlineImageNode {
    return {
      type: "inline-image",
      version: 1,
      height: this.__height ?? 0,
      width: this.__width ?? 0,
      src: this.__src,
      link: this.__link,
      isRounded: this.__isRounded,
      fileName: this.__fileName,
      fileType: this.__fileType,
      topText: this.__topText,
      topTextColor: this.__topTextColor,
      bottomText: this.__bottomText,
      bottomTextColor: this.__bottomTextColor,
    }
  }

  override createDOM(): HTMLElement {
    const span = document.createElement("span")

    span.style.display = "inline-flex"
    span.style.maxWidth = "100%"
    span.className = INLINE_IMAGE_CLASSNAME

    return span
  }

  override updateDOM(): false {
    return false
  }

  override decorate(): JSX.Element {
    return (
      <InlineImageComponent
        src={this.__src}
        nodeKey={this.getKey()}
        link={this.__link}
        width={this.__width}
        isRounded={this.__isRounded}
        fileName={this.__fileName}
        fileType={this.__fileType}
        topText={this.__topText}
        topTextColor={this.__topTextColor}
        bottomText={this.__bottomText}
        bottomTextColor={this.__bottomTextColor}
        onImageSettingsChange={this.__handleImageSettingsChange}
      />
    )
  }

  private __handleImageSettingsChange(settings: ImageSettingsForm) {
    if (
      settings.width !== this.__width ||
      settings.isRounded !== this.__isRounded ||
      settings.link !== this.__link ||
      settings.topText !== this.__topText ||
      settings.topTextColor !== this.__topTextColor ||
      settings.bottomText !== this.__bottomText ||
      settings.bottomTextColor !== this.__bottomTextColor
    ) {
      const writable = this.getWritable()

      console.log("WRITING SETTINGS", settings)

      writable.__width = settings.width
      writable.__isRounded = settings.isRounded
      writable.__link = settings.link
      writable.__topText = settings.topText
      writable.__topTextColor = settings.topTextColor
      writable.__bottomText = settings.bottomText
      writable.__bottomTextColor = settings.bottomTextColor
    }
  }
}

export function $createInlineImageNode({
  src,
  key,
  width,
  height,
  link,
  isRounded,
  fileName,
  fileType,
  topText,
  topTextColor,
  bottomText,
  bottomTextColor,
}: InlineImagePayload): InlineImageNode {
  return $applyNodeReplacement(
    new InlineImageNode(
      src,
      key,
      link,
      width,
      height,
      isRounded,
      fileName,
      fileType,
      topText,
      topTextColor,
      bottomText,
      bottomTextColor
    )
  )
}

export function $isInlineImageNode(node: LexicalNode | null | undefined): node is InlineImageNode {
  return node instanceof InlineImageNode
}

function convertInlineImageElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLImageElement) {
    const { src, style } = domNode

    const topTextColor = domNode.getAttribute("data-top-text-color") as string

    const link = domNode.getAttribute("data-link") as string
    const topText = domNode.getAttribute("data-top-text") as string
    const bottomText = domNode.getAttribute("data-bottom-text") as string
    const bottomTextColor = domNode.getAttribute("data-bottom-text-color") as string
    const width = parseInt(style.width) || undefined
    const height = parseInt(style.height) || undefined
    const rounded = style.borderRadius.includes("50%")

    const node = $createInlineImageNode({
      src,
      width,
      height,
      link,
      topText,
      topTextColor,
      bottomText,
      bottomTextColor,
      isRounded: rounded,
      fileName: domNode.getAttribute("data-file-name") ?? undefined,
      fileType: domNode.getAttribute("data-file-type") ?? undefined,
    })

    return { node }
  }
  return null
}

export const INLINE_IMAGE_CLASSNAME = "inline-image-lexical"
